/**
 * Componente modal para cambiar la contraseña de un usuario
 * Reutiliza MegaCard y componentes del LoginPage
 * Solo accesible para usuarios con rol admin
 */

import React, { useState } from 'react';
import { supabase } from '../../config/supabase';
import FormInput from '../../features/styled-components/FormElements/FormInput/FormInput';
import MegaCard, { MegaCardBody, MegaCardFooter, MegaCardHead } from '../../features/styled-components/Card/MegaCard';
import FormAlertDanger from '../../features/styled-components/FormElements/Alerts/FormAlert';
import MegaButton from '../../features/styled-components/Buttons/MegaButton/MegaButton';
import { RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import toast from 'react-hot-toast';

/**
 * Componente UserChangePassword
 * @param {object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Si el modal está abierto
 * @param {Function} props.onClose - Función para cerrar el modal
 * @param {string} props.userId - ID del usuario al que se le cambiará la contraseña
 * @param {string} props.userEmail - Email del usuario (para mostrar en el modal)
 */
const UserChangePassword = ({ isOpen, onClose, userId, userEmail }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isChanging, setIsChanging] = useState(false);

  // Maneja el evento de presionar Enter en los inputs del formulario.
  // Ejecuta la función de cambiar contraseña si ambos campos están completos.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && newPassword.length >= 6 && newPassword === confirmPassword) {
      handleChangePassword();
    }
  };

  // Cambia la contraseña del usuario usando la función RPC de Supabase.
  // Valida que las contraseñas coincidan y tengan al menos 6 caracteres.
  const handleChangePassword = async () => {
    setErrorMessage(null);

    // Validaciones
    if (newPassword.length < 6) {
      setErrorMessage({ message: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage({ message: 'Las contraseñas no coinciden' });
      return;
    }

    setIsChanging(true);

    try {
      // Llama a la función RPC admin_change_user_password
      const { error } = await supabase.rpc('admin_change_user_password', {
        user_id: userId,
        new_password: newPassword
      });

      if (error) {
        throw error;
      }

      // Éxito
      toast.success('Contraseña cambiada exitosamente');
      onClose();
      // Limpiar campos
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      setErrorMessage({ message: error.message || 'Error al cambiar la contraseña' });
    } finally {
      setIsChanging(false);
    }
  };

  // Si el modal está cerrado, no renderiza nada
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <MegaCard otraClassname='w-full max-w-md'>
        <MegaCardHead>
          <h3>Cambiar Contraseña</h3>
        </MegaCardHead>
        <MegaCardBody>
          <p className="text-sm mb-4">
            Usuario: {userEmail}
          </p>

          <div className='mb-4 relative'>
            <FormInput
              labelText={'Nueva contraseña'}
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='absolute right-3 top-10 hover:text-lila transition-colors'
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <RiEyeOffLine size={20} /> : <RiEyeLine size={20} />}
            </button>
          </div>

          <div className='mb-4 relative'>
            <FormInput
              labelText={'Confirmar contraseña'}
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              type='button'
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className='absolute right-3 top-10 hover:text-lila transition-colors'
              aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showConfirmPassword ? <RiEyeOffLine size={20} /> : <RiEyeLine size={20} />}
            </button>
          </div>

          {errorMessage && <FormAlertDanger labelText={errorMessage.message} onClick={() => setErrorMessage(null)} />}
        </MegaCardBody>

        <MegaCardFooter>
          <section className='flex justify-end gap-4'>
            <MegaButton
              labelText={'Cancelar'}
              className='bg-gray-500 text-white px-4 transition-all duration-300'
              onClick={onClose}
              disabled={isChanging}
            />
            <MegaButton
              loading={isChanging}
              labelText={'Cambiar'}
              className={isChanging ? 'bg-lila text-white' : 'bg-lila text-white px-4 transition-all duration-300'}
              onClick={handleChangePassword}
              disabled={!newPassword || !confirmPassword || newPassword.length < 6 || newPassword !== confirmPassword}
            />
          </section>
        </MegaCardFooter>
      </MegaCard>
    </div>
  );
};

export default UserChangePassword;
